"use client";

import { useCallback, useEffect, useState } from "react";
import { useHomepageTabsQuery, useTabRailsQuery } from "../api/queries";
import { homepageService } from "../services";
import type { IHomepageRail, IHomepageTab } from "../types";

interface IUseHomepageTabsArgs {
  /** Server render (no token): only the first paint, replaced by the client fetch. */
  initialTabs: IHomepageTab[];
  initialSlug: string | null;
  initialRails: IHomepageRail[];
  /** On a tab's own page the URL names the tab, so the visitor's default must not override it. */
  lockInitial?: boolean;
}

/**
 * Homepage tabs and rails. The data is personalized per visitor, so the client always
 * refetches with the visitor's token; the server props only fill the first paint.
 */
export function useHomepageTabs({
  initialTabs,
  initialSlug,
  initialRails,
  lockInitial = false,
}: IUseHomepageTabsArgs) {
  const [pickedSlug, setPickedSlug] = useState<string | null>(
    lockInitial ? initialSlug : null,
  );

  const { data: tabsData } = useHomepageTabsQuery({
    tabs: initialTabs,
    defaultTab: initialSlug,
  });
  const tabs = tabsData?.tabs ?? initialTabs;
  const selectedSlug = homepageService.resolveSelectedSlug(
    tabs,
    pickedSlug,
    tabsData?.defaultTab,
  );

  const { data: rails, isLoading } = useTabRailsQuery(
    selectedSlug,
    selectedSlug === initialSlug ? initialRails : undefined,
  );

  // A real navigation to another tab page brings new server props; follow them.
  useEffect(() => {
    if (lockInitial) setPickedSlug(initialSlug);
  }, [lockInitial, initialSlug]);

  // Switch without navigating; the address bar follows the tab so a copied link opens it.
  const selectTab = useCallback((tab: IHomepageTab) => {
    setPickedSlug(tab.slug);
    if (typeof window !== "undefined") {
      // No state of ours: Next copies its own in and its router follows the URL.
      window.history.replaceState(null, "", homepageService.tabHref(tab));
    }
  }, []);

  return {
    tabs,
    selectedTab: tabs.find((tab) => tab.slug === selectedSlug) ?? null,
    isInitialTab: selectedSlug === initialSlug,
    rails: rails ?? [],
    isLoading,
    selectTab,
  };
}
